import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  useToast,
} from '@/components/base';

import {
  pdfDocumentExporter,
} from '@/lib/exporters/pdfExporter';

import type {
  DocumentExportBranding,
} from '@/lib/documentExport';

import type {
  GeneratedDocument,
} from '@/hooks/useGeneratedDocuments';

type SignerForm = {
  name:
    string;

  email:
    string;

  phone:
    string;
};

type SignatureRequest = {
  id:
    string;

  status:
    string;

  sandbox?:
    number;

  signed_file_url?:
    string | null;

  signers?:
    Array<{
      id:
        string;

      name:
        string;

      email?:
        string | null;

      status:
        string;

      signing_url?:
        string | null;
    }>;
};

const emptySigner = ():
  SignerForm => ({
  name:
    '',

  email:
    '',

  phone:
    '',
});

const readError = async (
  response: Response
) => {
  try {
    const body =
      await response
        .json() as {
          error?:
            string;
        };

    return body.error ||
      `HTTP ${response.status}`;
  }
  catch {
    return `HTTP ${response.status}`;
  }
};

const statusLabel = (
  value: string
) => {
  const labels:
    Record<
      string,
      string
    > = {
    pending:
      'Pendente',

    completed:
      'Concluída',

    rejected:
      'Recusada',

    cancelled:
      'Cancelada',

    error:
      'Erro',

    viewed:
      'Visualizado',

    signed:
      'Assinado',

    delivery_failed:
      'Falha no envio',
  };

  return labels[value] ??
    value;
};

export default function SignatureRequestButton(
  {
    document,
    branding,
  }: {
    document:
      GeneratedDocument;

    branding:
      DocumentExportBranding;
  }
) {
  const {
    addToast,
  } =
    useToast();

  const [
    open,
    setOpen,
  ] =
    useState(false);

  const [
    signers,
    setSigners,
  ] =
    useState<
      SignerForm[]
    >([
      emptySigner(),
    ]);

  const [
    message,
    setMessage,
  ] =
    useState('');

  const [
    sandbox,
    setSandbox,
  ] =
    useState(false);

  const [
    submitting,
    setSubmitting,
  ] =
    useState(false);

  const [
    loading,
    setLoading,
  ] =
    useState(false);

  const [
    syncingId,
    setSyncingId,
  ] =
    useState<
      string |
      null
    >(null);

  const [
    requests,
    setRequests,
  ] =
    useState<
      SignatureRequest[]
    >([]);

  const loadRequests =
    useCallback(
      async () => {
        setLoading(
          true
        );

        try {
          const response =
            await fetch(
              `/api/signatures?generatedDocumentId=${encodeURIComponent(
                document.id
              )}`,
              {
                credentials:
                  'same-origin',
              }
            );

          if (!response.ok) {
            throw new Error(
              await readError(
                response
              )
            );
          }

          const body =
            await response
              .json() as {
                requests?:
                  SignatureRequest[];
              };

          setRequests(
            Array.isArray(
              body.requests
            )
              ? body.requests
              : []
          );
        }
        catch (
          error
        ) {
          addToast(
            error instanceof Error
              ? error.message
              : 'Não foi possível carregar as assinaturas.',
            'error'
          );
        }
        finally {
          setLoading(
            false
          );
        }
      },
      [
        addToast,
        document.id,
      ]
    );

  useEffect(
    () => {
      if (open) {
        void loadRequests();
      }
    },
    [
      loadRequests,
      open,
    ]
  );

  if (
    document.status !==
    'finalized'
  ) {
    return null;
  }

  const updateSigner = (
    index: number,
    field: keyof SignerForm,
    value: string
  ) => {
    setSigners(
      (
        current
      ) =>
        current.map(
          (
            signer,
            signerIndex
          ) =>
            signerIndex ===
            index
              ? {
                  ...signer,

                  [field]:
                    value,
                }
              : signer
        )
    );
  };

  const addSigner =
    () => {
      setSigners(
        (
          current
        ) => [
          ...current,
          emptySigner(),
        ]
      );
    };

  const removeSigner = (
    index: number
  ) => {
    setSigners(
      (
        current
      ) =>
        current.filter(
          (
            _,
            signerIndex
          ) =>
            signerIndex !==
            index
        )
    );
  };

  const submit =
    async () => {
      if (
        submitting
      ) {
        return;
      }

      const normalized =
        signers.map(
          (
            signer
          ) => ({
            name:
              signer.name
                .trim(),

            email:
              signer.email
                .trim(),

            phone:
              signer.phone
                .trim(),
          })
        );

      if (
        normalized.some(
          (
            signer
          ) =>
            !signer.name ||
            !signer.email
        )
      ) {
        addToast(
          'Informe nome e e-mail para todos os signatários.',
          'warning'
        );

        return;
      }

      const content =
        document
          .renderedContent
          ?.trim() ||
        document.content ||
        '';

      if (
        !content.trim()
      ) {
        addToast(
          'O documento não possui conteúdo para assinatura.',
          'warning'
        );

        return;
      }

      setSubmitting(
        true
      );

      try {
        const artifact =
          await pdfDocumentExporter
            .export({
              title:
                document.title,

              content,

              creator:
                'BM Legal OS',

              subject:
                'Documento para assinatura eletrônica',

              branding,
            });

        const form =
          new FormData();

        form.append(
          'generatedDocumentId',
          document.id
        );

        form.append(
          'signers',
          JSON.stringify(
            normalized
          )
        );

        form.append(
          'message',
          message.trim()
        );

        form.append(
          'sandbox',
          sandbox
            ? 'true'
            : 'false'
        );

        form.append(
          'file',
          artifact.blob,
          artifact.fileName
        );

        const response =
          await fetch(
            '/api/signatures',
            {
              method:
                'POST',

              credentials:
                'same-origin',

              body:
                form,
            }
          );

        if (!response.ok) {
          throw new Error(
            await readError(
              response
            )
          );
        }

        addToast(
          sandbox
            ? 'Documento sandbox enviado ao Autentique.'
            : 'Documento enviado para assinatura.',
          'success'
        );

        setMessage('');
        setSigners([
          emptySigner(),
        ]);

        await loadRequests();
      }
      catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível enviar o documento para assinatura.',
          'error'
        );
      }
      finally {
        setSubmitting(
          false
        );
      }
    };

  const sync =
    async (
      id: string
    ) => {
      setSyncingId(
        id
      );

      try {
        const response =
          await fetch(
            `/api/signatures/${encodeURIComponent(
              id
            )}/sync`,
            {
              method:
                'POST',

              credentials:
                'same-origin',
            }
          );

        if (!response.ok) {
          throw new Error(
            await readError(
              response
            )
          );
        }

        await loadRequests();

        addToast(
          'Status da assinatura sincronizado.',
          'success'
        );
      }
      catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível sincronizar a assinatura.',
          'error'
        );
      }
      finally {
        setSyncingId(
          null
        );
      }
    };

  return (
    <>
      <button
        type="button"
        onClick={(
          event
        ) => {
          event.stopPropagation();
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer"
        title="Enviar para assinatura eletrônica"
      >
        <i className="ri-quill-pen-line"></i>
        <span>Assinar</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
          onClick={(
            event
          ) => {
            event.stopPropagation();

            if (
              event.target ===
              event.currentTarget
            ) {
              setOpen(false);
            }
          }}
        >
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-background-50 border border-background-200 shadow-xl p-5 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-foreground-900">
                  Assinatura eletrônica
                </h3>

                <p className="text-sm text-foreground-500 mt-1">
                  {document.title}
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setOpen(false)
                }
                className="w-9 h-9 rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100"
                aria-label="Fechar"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900">
                    Signatários
                  </h4>

                  <p className="text-xs text-foreground-500">
                    O PDF é gerado no navegador e transmitido diretamente ao provider.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={addSigner}
                  className="px-3 py-2 rounded-lg border border-background-200 text-xs font-medium text-foreground-700 hover:bg-background-100"
                >
                  + Signatário
                </button>
              </div>

              {signers.map(
                (
                  signer,
                  index
                ) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 p-3 rounded-xl border border-background-200"
                  >
                    <input
                      value={
                        signer.name
                      }
                      onChange={(
                        event
                      ) =>
                        updateSigner(
                          index,
                          'name',
                          event.target.value
                        )
                      }
                      className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm"
                      placeholder="Nome"
                    />

                    <input
                      value={
                        signer.email
                      }
                      onChange={(
                        event
                      ) =>
                        updateSigner(
                          index,
                          'email',
                          event.target.value
                        )
                      }
                      className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm"
                      placeholder="E-mail"
                      type="email"
                    />

                    <input
                      value={
                        signer.phone
                      }
                      onChange={(
                        event
                      ) =>
                        updateSigner(
                          index,
                          'phone',
                          event.target.value
                        )
                      }
                      className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm"
                      placeholder="Telefone opcional"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        removeSigner(
                          index
                        )
                      }
                      disabled={
                        signers.length ===
                        1
                      }
                      className="px-3 py-2 rounded-lg border border-red-200 text-red-600 text-xs disabled:opacity-40"
                    >
                      Remover
                    </button>
                  </div>
                )
              )}
            </div>

            <label className="space-y-1 block">
              <span className="text-xs font-medium text-foreground-600">
                Mensagem
              </span>

              <textarea
                value={
                  message
                }
                onChange={(
                  event
                ) =>
                  setMessage(
                    event.target.value
                  )
                }
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm min-h-20"
                placeholder="Mensagem opcional enviada aos signatários."
              />
            </label>

            <label className="inline-flex items-center gap-2 text-sm text-foreground-700">
              <input
                type="checkbox"
                checked={
                  sandbox
                }
                onChange={(
                  event
                ) =>
                  setSandbox(
                    event.target.checked
                  )
                }
              />

              Usar sandbox do Autentique
            </label>

            <button
              type="button"
              onClick={() =>
                void submit()
              }
              disabled={
                submitting
              }
              className="w-full px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60"
            >
              {submitting
                ? 'Enviando PDF...'
                : 'Enviar para assinatura'}
            </button>

            <div className="border-t border-background-200 pt-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-foreground-900">
                  Histórico de assinaturas
                </h4>

                <button
                  type="button"
                  onClick={() =>
                    void loadRequests()
                  }
                  disabled={
                    loading
                  }
                  className="text-xs font-medium text-primary-600"
                >
                  {loading
                    ? 'Atualizando...'
                    : 'Atualizar'}
                </button>
              </div>

              {requests.length ===
              0 ? (
                <p className="text-xs text-foreground-500">
                  Nenhuma solicitação enviada para este documento.
                </p>
              ) : (
                requests.map(
                  (
                    request
                  ) => (
                    <div
                      key={
                        request.id
                      }
                      className="rounded-xl border border-background-200 p-3 space-y-2"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-xs text-foreground-700">
                          <strong>
                            {statusLabel(
                              request.status
                            )}
                          </strong>

                          {request.sandbox
                            ? ' · sandbox'
                            : ''}
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            void sync(
                              request.id
                            )
                          }
                          disabled={
                            syncingId ===
                            request.id
                          }
                          className="px-2 py-1 rounded-lg border border-background-200 text-[11px] font-medium"
                        >
                          {syncingId ===
                          request.id
                            ? 'Sincronizando...'
                            : 'Sincronizar'}
                        </button>
                      </div>

                      {request.signers?.map(
                        (
                          signer
                        ) => (
                          <div
                            key={
                              signer.id
                            }
                            className="flex flex-wrap items-center justify-between gap-2 text-xs"
                          >
                            <span className="text-foreground-600">
                              {signer.name}
                              {' · '}
                              {statusLabel(
                                signer.status
                              )}
                            </span>

                            {signer.signing_url && (
                              <a
                                href={
                                  signer.signing_url
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 font-medium"
                              >
                                Abrir link
                              </a>
                            )}
                          </div>
                        )
                      )}

                      {request.signed_file_url && (
                        <a
                          href={
                            request.signed_file_url
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex text-xs font-medium text-emerald-700"
                        >
                          Abrir documento assinado
                        </a>
                      )}
                    </div>
                  )
                )
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}